import React, { useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import useClickOutside from '../../hooks/useClickOutside'
import useHoverIntent from '../../hooks/useHoverIntent'
import classes from './styles.module.scss'

/**
 * A reusable hover-activated popover with auto top/bottom positioning and arrow indicator.
 *
 * @example
 * <HoverPopover
 *   title="Help"
 *   width={360}
 *   trigger={({ ref, handlers }) => (
 *     <button ref={ref} {...handlers}>Hover me</button>
 *   )}
 * >
 *   <p>Popover body content here</p>
 * </HoverPopover>
 */
const HoverPopover = ({
    title,
    children,
    trigger,
    width = 400,
    closeDelay = 200,
    showCloseButton = true,
    className,
}) => {
    const triggerRef = useRef(null)
    const popoverRef = useRef(null)

    const { isOpen, open, close, triggerHandlers, floatingHandlers } = useHoverIntent({
        closeDelay,
    })

    useClickOutside(
        [triggerRef, popoverRef],
        close,
        isOpen,
    )

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current) return 'top'
        const rect = triggerRef.current.getBoundingClientRect()
        const popoverHeight = 200 // approximate
        return rect.top < popoverHeight ? 'bottom' : 'top'
    }, [])

    const handleTriggerClick = useCallback(() => {
        open()
    }, [open])

    const position = isOpen ? calculatePosition() : 'top'

    const popoverClassName = [
        position === 'bottom' ? classes.popoverBottom : classes.popover,
        className,
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div className={classes.wrapper}>
            {trigger({
                ref: triggerRef,
                handlers: {
                    ...triggerHandlers,
                    onClick: handleTriggerClick,
                },
            })}

            {isOpen && (
                <div
                    ref={popoverRef}
                    className={popoverClassName}
                    style={{ width }}
                    {...floatingHandlers}
                >
                    {(title || showCloseButton) && (
                        <div className={classes.header}>
                            {title && <span className={classes.title}>{title}</span>}
                            {showCloseButton && (
                                <button className={classes.closeButton} onClick={close}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M12 4L4 12M4 4L12 12"
                                            stroke="rgba(7, 24, 34, 1)"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            )}
                        </div>
                    )}

                    {children}
                </div>
            )}
        </div>
    )
}

HoverPopover.propTypes = {
    /** Popover header title */
    title: PropTypes.string,
    /** Content rendered inside the popover body */
    children: PropTypes.node.isRequired,
    /** Render-prop for the trigger element. Receives `{ ref, handlers }` */
    trigger: PropTypes.func.isRequired,
    /** Popover width in px */
    width: PropTypes.number,
    /** Delay in ms before popover closes after mouse leaves */
    closeDelay: PropTypes.number,
    /** Whether to show the × close button */
    showCloseButton: PropTypes.bool,
    /** Additional class name for the popover panel */
    className: PropTypes.string,
}

export default HoverPopover
